import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import GlassModal from './Common/GlassModal';
import { showToast } from './Common/Toast';
import { validateTimeOffRange } from '../utils/timeOffValidation';

import { utcDateKey } from '../utils/dateFormatters';
const PTOEditModal = ({ isOpen, onClose, onSubmit, ptoRequest, employee = null }) => {
  const [formData, setFormData] = useState({
    kind: 'VACATION',
    startDate: '',
    endDate: '',
    isFullDay: true,
    startTime: '09:00',
    endTime: '17:00',
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [rangeError, setRangeError] = useState('');

  // Initialize form with existing PTO data
  useEffect(() => {
    if (ptoRequest && isOpen) {
      // Parse the starts_at and ends_at to extract dates and times
      const startsAt = new Date(ptoRequest.starts_at);
      const endsAt = new Date(ptoRequest.ends_at);

      // Check if it's a full day request (times are near midnight)
      const isFullDay = startsAt.getUTCHours() === 0 && startsAt.getUTCMinutes() === 0 &&
                        endsAt.getUTCHours() === 23 && endsAt.getUTCMinutes() === 59;

      setFormData({
        kind: ptoRequest.kind || 'VACATION',
        // Naive wall clock parked at UTC (see formatWallClockDate): the UTC day IS the date.
        startDate: utcDateKey(startsAt),
        endDate: utcDateKey(endsAt),
        isFullDay: isFullDay,
        startTime: isFullDay ? '09:00' : `${String(startsAt.getUTCHours()).padStart(2, '0')}:${String(startsAt.getUTCMinutes()).padStart(2, '0')}`,
        endTime: isFullDay ? '17:00' : `${String(endsAt.getUTCHours()).padStart(2, '0')}:${String(endsAt.getUTCMinutes()).padStart(2, '0')}`,
        note: ptoRequest.note || ''
      });
      setRangeError('');
    }
  }, [ptoRequest, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Board #1945: Edit accepted an inverted range just as Create did. Same shared rule, so the
    // two cannot drift — these modals already build their startsAt/endsAt strings independently.
    const message = validateTimeOffRange(formData);
    if (message) {
      setRangeError(message);
      return;
    }
    setRangeError('');
    setLoading(true);

    try {
      // Convert form data to proper datetime format
      const submitData = {
        ...formData,
        startsAt: formData.isFullDay
          ? `${formData.startDate}T00:00:00`
          : `${formData.startDate}T${formData.startTime}:00`,
        endsAt: formData.isFullDay
          ? `${formData.endDate}T23:59:59`
          : `${formData.endDate}T${formData.endTime}:00`
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error updating PTO request:', error);
      if (window.alert) {
        showToast('error', 'Failed to update PTO request:');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setRangeError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      closeOnOverlay={false}
      header={(
        <>
          <h2 className="text-xl font-semibold text-gray-900">Edit Time Off Request</h2>
          <button type="button" aria-label="Close" title="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </>
      )}
      footer={(
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-900 dark:text-gray-100 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 hover:bg-blue-50/50 dark:hover:bg-blue-900/20"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="pto-edit-form"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Time Off'}
          </button>
        </div>
      )}
    >
      <form id="pto-edit-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Board #1945/#1947: stated beside the fields, and role="alert" so it is announced
            rather than only seen. */}
        {rangeError && (
          <div role="alert" className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-800 dark:text-red-200">
            {rangeError}
          </div>
        )}
        {/* Show current user/employee info */}
        <div className="bg-white/5 backdrop-blur-sm p-3 rounded-md border border-white/10">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Editing Request For
          </label>
          <p className="text-sm text-gray-900 font-medium">
            {employee?.full_name ||
              (employee?.first_name && employee?.last_name ? `${employee.first_name} ${employee.last_name}` :
              'Employee')}
          </p>
        </div>

        {/* Time Off Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            name="kind"
            value={formData.kind}
            onChange={handleChange}
            className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {/* Board #3908. This list drifted from the one Add Time Off writes with, so a
                record whose kind it did not contain had no matching <option> and the select
                displayed the FIRST one instead — an OTHER entry read as Vacation, and saving
                would have written that back. Two gaps: OTHER was missing entirely, and this
                offered JURY while the create path writes JURY_DUTY. Live 2026-09-30: the
                enum has both, 1 row is JURY_DUTY, 1 is OTHER, and ZERO rows are JURY — so
                JURY was an option nobody could legitimately be editing.
                These now match PTORequestModal exactly, in the same order. */}
            <option value="VACATION">🏖️ Vacation</option>
            <option value="SICK">🤒 Sick Leave</option>
            <option value="PERSONAL">👤 Personal Day</option>
            <option value="HOLIDAY">🎉 Holiday</option>
            <option value="BEREAVEMENT">🕊️ Bereavement</option>
            <option value="JURY_DUTY">⚖️ Jury Duty</option>
            <option value="UNPAID">📝 Unpaid Leave</option>
            <option value="FMLA">⚕️ FMLA Leave</option>
            <option value="OTHER">📝 Other</option>
            {/* A legacy JURY row would otherwise hit the same silent-relabel bug this fixes.
                Shown only when the record already carries it, so it cannot be chosen anew. */}
            {formData.kind === 'JURY' && <option value="JURY">⚖️ Jury Duty (legacy)</option>}
          </select>
        </div>

        {/* Full Day Toggle */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="isFullDay"
              checked={formData.isFullDay}
              onChange={(e) => { setRangeError(''); setFormData({...formData, isFullDay: e.target.checked}); }}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Full Day</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {formData.isFullDay ? 'Entire day will be blocked' : 'Specify start and end times'}
          </p>
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            required
            className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            required
            className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Time Selection (only if not full day) */}
        {!formData.isFullDay && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note (Optional)
          </label>
          <textarea
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={3}
            placeholder="Reason for time off..."
            className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </form>
    </GlassModal>
  );
};

export default PTOEditModal;
